import { useState, useEffect, useMemo, use } from "react";

import {
  FiTrash2,
  FiPlus,
  FiEdit2,
  FiCopy,
  FiChevronDown,
  FiTrash,
  FiSettings,
} from "react-icons/fi";
import { useShow } from "@/contexts/ShowContext";
import { apiFetch } from "@/lib/api";
import Button from "@/components/ui/Button";
import EpisodeEditorModal from "@/components/modals/EpisodeEditorModal";
import ConfirmModal from "@/components/modals/ConfirmModal";
import { useModal } from "@/contexts/ModalContext";
import { useNavigate } from "react-router-dom";
import { UserRole } from "@/contexts/UserContext";
import useFileUpload from "@/hooks/useFileUpload";
import Loader from "@/components/loader";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/shadcn/dropdown-menu";
import { Button as ShadButton } from "@/components/ui/shadcn/button";
import NewEpisodeModal from "@/components/modals/NewEpisodeModal";
import useKagamiBridge from "@/hooks/useKagamiBridge";
import SafeImage from "@/components/ui/SafeImage";

export default function EpisodesPage() {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingStatus, setLoadingStatus] = useState("");
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const { show } = useShow();
  const { openModal } = useModal();
  const navigate = useNavigate();
  const { uploadFile, uploadProgress, uploadError } = useFileUpload();
  const [showNewEpisode, setShowNewEpisode] = useState(false);
  const { runTaskAndPoll, taskStatus, taskState } = useKagamiBridge();

  const getEpisodes = async () => {
    try {
      setUpdating(true);
      const episodesResponse = await apiFetch(
        `/api/shows/${show._id}/episodes`,
      );
      if (!episodesResponse.ok) {
        throw new Error("Failed to fetch episodes");
      }
      const episodesData = await episodesResponse.json();
      setEpisodes(episodesData);
    } catch (error) {
      setError(error.message);
    } finally {
      setUpdating(false);
    }
  };

  const getData = async () => {
    try {
      setLoading(true);
      await getEpisodes();
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoadingStatus(taskStatus);
  }, [taskStatus, taskState]);

  useEffect(() => {
    getData();
  }, [show]);

  const handleEditEpisode = async (episode) => {
    const episodeData = await openModal(
      <EpisodeEditorModal episode={episode} />,
      "side",
    );

    if (!episodeData) return;

    setUpdating(true);
    try {
      if (episodeData.thumbnailFile) {
        const uploadResponse = await uploadFile(episodeData.thumbnailFile);
        episodeData.thumbnail_url = uploadResponse.url;
        episodeData.thumbnail_key = uploadResponse.key;
        delete episodeData.thumbnailFile;
      }

      const response = await apiFetch(
        episode
          ? `/api/shows/${show._id}/episodes/${episode._id}`
          : `/api/shows/${show._id}/episodes`,
        {
          method: episode ? "PUT" : "POST",
          body: JSON.stringify(episodeData),
        },
      );
      if (response.ok) {
        await getEpisodes();
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (episode) => {
    const message = `Are you sure you want to delete "${episode.name}"?`;

    const confirmed = await openModal(
      <ConfirmModal
        title="Delete Episode"
        type="destructive"
        message={message}
      />,
    );

    if (confirmed) {
      try {
        setUpdating(true);
        const response = await apiFetch(
          `/api/shows/${show._id}/episodes/${episode._id}`,
          {
            method: "DELETE",
          },
        );

        if (response.ok) {
          await getEpisodes();
        } else {
          setError(response.statusText);
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setUpdating(false);
      }
    }
  };

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
        <span className="text-red-400">Error: {error}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="flex justify-between items-center px-10 h-20">
        <div className="flex gap-2 items-center">
          <h1 className="text-2xl font-bold border-l-4 border-primary pl-4">
            Episodes
          </h1>
        </div>
        <div className="flex gap-2">
          <UserRole role="editor">
            <Button
              type="secondary"
              icon={<FiPlus className="w-4 h-4" />}
              onClick={handleEditEpisode}>
              New Episode
            </Button>
          </UserRole>
        </div>
      </div>
      <div className="flex flex-col flex-1 overflow-y-auto">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 px-10 py-4 overflow-y-auto">
          {episodes.map((episode) => (
            <div
              key={episode._id}
              className="flex flex-col gap-2 relative group cursor-pointer"
              onClick={() => navigate(`/show/episodes/${episode._id}`)}>
              <div className="relative aspect-video border rounded-md bg-card overflow-hidden flex items-center justify-center">
                <SafeImage
                  src={episode.thumbnail_url}
                  alt={episode.name}
                  fill
                  className="object-cover"
                />

                <div className="absolute gap-1 right-0 bottom-0 p-1 hidden group-hover:flex">
                  <UserRole role="editor">
                    <div className="flex items-center bg-card rounded-lg border">
                      <div
                        className="p-2 flex items-center justify-center text-secondary-foreground/80 hover:text-secondary-foreground cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditEpisode(episode);
                        }}>
                        <FiSettings className="w-3 h-3" />
                      </div>
                      <div
                        className="p-2 flex items-center justify-center text-destructive-foreground/80 hover:text-destructive-foreground cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(episode);
                        }}>
                        <FiTrash className="w-3 h-3" />
                      </div>
                    </div>
                  </UserRole>
                </div>
              </div>
              <div className="flex flex-col p-2">
                <span className="text-lg font-bold">{episode.name}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-secondary-foreground/50">
                    {episode._id}
                  </span>
                  <div
                    className="text-[10px] text-secondary-foreground/80 cursor-pointer hover:text-secondary-foreground"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(episode._id);
                    }}>
                    <FiCopy className="w-3 h-3" />
                  </div>
                </div>
                <span className="text-sm text-secondary-foreground">
                  {episode.description}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {updating && <Loader status={loadingStatus} />}
    </div>
  );
}
